import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Plus, Search, Grid, List, Calendar as CalendarIcon, Map, Lock, Mountain } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEvents, useUserEvents, Event } from "@/hooks/useEvents";
import EventCard from "@/components/EventCard";
import { EventListItem } from "@/components/EventListItem";
import { EventsCalendar } from "@/components/EventsCalendar";
import { EventsMap } from "@/components/EventsMap";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useMemo } from "react";

const Events = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccess, tier } = useSubscription();
  const { data: allEvents, isLoading: allEventsLoading } = useEvents();
  const { data: userEvents, isLoading: userEventsLoading } = useUserEvents(user?.id);

  const canAccessEvents = canAccess('organizer');

  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<Event['status']>('upcoming');
  const [sortBy, setSortBy] = useState<'date' | 'created' | 'popular'>('date');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'calendar' | 'map'>('grid');
  const [showMyEvents, setShowMyEvents] = useState(false);

  const isLoading = allEventsLoading || userEventsLoading;

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let events = showMyEvents 
      ? [...(userEvents?.hosting || []), ...(userEvents?.attending || [])]
      : allEvents || [];

    // Filter by status
    if (statusFilter !== 'upcoming') {
      events = events.filter(e => e.status === statusFilter);
    } else {
      events = events.filter(e => e.status === 'upcoming');
    }

    // Filter by event type
    if (eventTypeFilter !== 'all') {
      events = events.filter(e => e.event_type === eventTypeFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      events = events.filter(e => 
        e.title.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query) ||
        e.meeting_point_description?.toLowerCase().includes(query)
      );
    }

    // Sort events
    events = [...events].sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'popular':
          // Would need participant count - placeholder for now
          return 0;
        default:
          return 0;
      }
    });

    return events;
  }, [allEvents, userEvents, showMyEvents, statusFilter, eventTypeFilter, searchQuery, sortBy]);

  // Show upgrade prompt for non-organizers
  if (user && !canAccessEvents) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card className="border-primary/20">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">Events are an Organizer Feature</CardTitle>
                <CardDescription className="text-base mt-2">
                  Host group rides, trail cleanups, meetups, and more with our Events feature.
                  Available exclusively for Organizer tier members.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-2">Coming Soon!</p>
                  <p>
                    The Organizer tier is currently in development. Join the waitlist to be notified
                    when it launches.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => navigate('/pricing')} variant="outline">
                    <Mountain className="h-4 w-4 mr-2" />
                    View Plans
                  </Button>
                  <Button onClick={() => navigate('/dashboard')}>
                    Back to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground">Events</h1>
            <p className="text-muted-foreground mt-2">
              Join the community for rides, meetups, and more
            </p>
          </div>
          {user && (
            <Button onClick={() => navigate('/events/create')}>
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          )}
        </div>

        {/* Filters and Search */}
        <div className="space-y-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="ride">Ride</SelectItem>
                <SelectItem value="garage_day">Garage Day</SelectItem>
                <SelectItem value="cleanup">Cleanup</SelectItem>
                <SelectItem value="meetup">Meetup</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="overlanding_trip">Overlanding Trip</SelectItem>
                <SelectItem value="competition">Competition</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(value: Event['status']) => setStatusFilter(value)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(value: 'date' | 'created' | 'popular') => setSortBy(value)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="created">Recently Created</SelectItem>
                <SelectItem value="popular">Popular</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {user && (
                <Button
                  variant={showMyEvents ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowMyEvents(!showMyEvents)}
                >
                  My Events
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('calendar')}
              >
                <CalendarIcon className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('map')}
              >
                <Map className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Events Display */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        ) : filteredEvents.length > 0 ? (
          <>
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEvents.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )}

            {viewMode === 'list' && (
              <div className="space-y-4">
                {filteredEvents.map((event) => (
                  <EventListItem key={event.id} event={event} />
                ))}
              </div>
            )}

            {viewMode === 'calendar' && (
              <EventsCalendar events={filteredEvents} />
            )}

            {viewMode === 'map' && (
              <EventsMap events={filteredEvents} className="h-[600px]" />
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg mb-4">
              {searchQuery || eventTypeFilter !== 'all' 
                ? 'No events match your filters' 
                : 'No events yet'}
            </p>
            {user && !showMyEvents && (
              <Button onClick={() => navigate('/events/create')}>
                Create the First Event
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Events;
